import React, { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import TopBar from "../../components/TopBar";
import Table from "../../components/Table";
import Sidebar from "../../components/Sidebar";
import Spinner from "../../components/Spinner";
import {
  loadTickets,
  deleteMultipleTickets,
  download30DayArchive,
} from "../../redux/tasks/ticketsT";

const Tickets = ({
  loading,
  tickets,
  user,
  loadTickets,
  download30DayArchive,
  deleteMultipleTickets,
}) => {
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [ticketIds, setTicketIds] = useState([]);
  const [numberOptions] = useState([5, 10, 15, 20, 25, 30]);
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    loadTickets();
  }, [loadTickets, loading]);

  const handleDelete = (index) => {
    setLoad(true);
    deleteMultipleTickets(toast, setLoad, ticketIds, navigate);
  };

  const handleData = (index) => {
    const ticket = tickets[index];
    navigate("/single-ticket", { state: { ticket } });
  };

  const handleCheckboxChange = (e, ticket) => {
    e.stopPropagation();
    if (ticketIds.includes(ticket)) {
      setTicketIds(ticketIds.filter((item) => item !== ticket));
    } else {
      setTicketIds([...ticketIds, ticket]);
    }
  };

  const TableColumns = [
    {
      Header: ".",
      accessor: "checkbox",
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={ticketIds.includes(row.original._id)}
          onChange={(e) => handleCheckboxChange(e, row.original._id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      Header: "#",
      accessor: "ticketNo",
      Cell: ({ value, row }) => {
        const seenBy = row.original.seenBy;
        const isSeenByUser = Array.isArray(seenBy) && seenBy.includes(user._id);
        return (
          <div className="flex items-center gap-1">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                !isSeenByUser ? "bg-red-600" : "invisible"
              } animate-pulse`}
            />

            <span>{value}</span>
          </div>
        );
      },
    },
    {
      Header: "Subject",
      accessor: "subject",
    },
    {
      Header: "Department",
      accessor: "department",
    },
    {
      Header: "Status",
      accessor: "status",
    },
    {
      Header: "Priority",
      accessor: "priority",
      Cell: ({ value }) => {
        let textColor = "blue";

        switch (value?.toLowerCase()) {
          case "high":
            textColor = "#ef4444"; // red-500
            break;
          case "medium":
            textColor = "#f59e0b"; // amber-500
            break;
          case "low":
            textColor = "#10b981"; // green-500
            break;
          default:
            textColor = "black";
        }

        return (
          <span
            style={{
              color: textColor,
              // padding: "4px 8px",
              // borderRadius: "12px",
              // textTransform: "capitalize",
              fontWeight: 700,
            }}
          >
            {value || "N/A"}
          </span>
        );
      },
    },
    {
      Header: "Created at",
      accessor: "createdAt",
      Cell: ({ value }) => {
        // return format(new Date(value), "MM-dd-yyyy");
        if (!value) return "N/A";
        const age = formatDistanceToNow(new Date(value), { addSuffix: true });
        return <span>{age}</span>;
      },
    },
  ];

  return loading || load ? (
    <Spinner />
  ) : (
    <>
      <div className="flex flex-col h-screen">
        <TopBar />
        <div className="flex flex-1 overflow-hidden">
          <Sidebar />
          <main className="flex-1 p-4 overflow-y-auto">
            <div className=" mx-auto">
              <div className="flex flex-col gap-x-6 sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 sm:space-x-2 p-4">
                <button
                  onClick={() => download30DayArchive()}
                  className={`font-semibold py-2 px-9 rounded sm:order-1 bg-neutral-300 text-neutral-700 hover:bg-sky-800 hover:text-neutral-200`}
                >
                  30 Day Archive
                </button>
                <button
                  onClick={(e) => handleDelete(e)}
                  className={`font-semibold py-2 px-9 rounded sm:order-3 ${
                    ticketIds.length > 0
                      ? "bg-sysRed hover:bg-red-600 text-white"
                      : "bg-neutral-300 text-neutral-700"
                  }`}
                >
                  Delete
                </button>
                <div className="flex w-1/5 bg-blue-800 items-center border border-gray-300 rounded sm:order-2">
                  <input
                    type="text"
                    placeholder="Search"
                    className="p-2 w-full border-none outline-none"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                  />
                  <div className="p-2">
                    <FaSearch className="text-gray-500" />
                  </div>
                </div>
              </div>
              {tickets?.length > 0 ? (
                <Table
                  data={tickets}
                  columns={TableColumns}
                  numberOptions={numberOptions}
                  dataFun={handleData}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                />
              ) : (
                <div className="flex justify-center mt-10  text-2xl font-semibold">
                  {" "}
                  No Tickets Found!
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

Tickets.propTypes = {
  user: PropTypes.object,
  tickets: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  loadTickets: PropTypes.func.isRequired,
  download30DayArchive: PropTypes.func.isRequired,
  deleteMultipleTickets: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  loading: state.tickets.loading,
  tickets: state.tickets.tickets,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  loadTickets,
  deleteMultipleTickets,
  download30DayArchive,
})(Tickets);
